import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BASE_URL } from "../../constants";

const ticketServices = createApi({
  reducerPath: "ticket",
  tagType: "ticket-booking",
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const reducers = getState();
      const token = reducers?.authReducer?.token;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),

  endpoints: (builder) => {
    return {
      getTicketsInfo: builder.query({
        query: (id) => {
          return {
            url: `/validateBookingViaClassLink/${id}`,
            method: "GET",
          };
        },
        providesTags: ["ticket-booking"],
      }),
      getPaymentProfile: builder.query({
        query: (id) => {
          return {
            url: `/payments/getAvailablePaymentMethods?organizerId=${id}`,
            method: "GET",
          };
        },
        providesTags: ["ticket-booking"],
      }),

      getBookingConfirmation: builder.query({
        query: (id) => {
          return {
            url: `/getMybookings/${id}`,
            method: "GET",
          };
        },
        providesTags: ["ticket-booking"],
      }),

      getValidateWebLink: builder.query({
        query: (id) => {
          return {
            url: `/public/validateWebLink/${id}`,
            method: "GET",
          };
        },
        providesTags: ["ticket-booking"],
      }),

      checkStripePaymentStatus: builder.mutation({
        query: (data) => {
          return {
            url: "/payments/checkLinkStripePaymentStatus",
            method: "POST",
            body: JSON.stringify(data),
            headers: {
              "Content-Type": "application/json",
            },
          };
        },
      }),
      createTikkiePaymentLink: builder.mutation({
        query: (data) => {
          return {
            url: "/payments/createPaymentLinkUA",
            method: "POST",
            body: JSON.stringify(data),
            headers: {
              "Content-Type": "application/json",
            },
          };
        },
      }),
      sendEmail: builder.mutation({
        query: (data) => {
          return {
            url: "/sendBookingConfirmation",
            method: "POST",
            body: JSON.stringify(data),
            headers: {
              "Content-Type": "application/json",
            },
          };
        },
      }),

      getEventDetails: builder.query({
        query: ({ name, refId, campaignId }) =>
          `/schedule/get?eventName=${name}&referenceId=${refId}${campaignId ? `&campaignId=${campaignId}` : ""}`,
      }),

      // Resolve an opaque tracked-link token into { sellerReferenceId, campaignId,
      // valid, ... } so the storefront can show only the campaign's tickets/add-ons.
      resolveTrackToken: builder.query({
        query: (token) => `/marketplace/resolve/${token}`,
      }),

      getBookingInfo: builder.query({
        query: (bookingId) => `/getBookingDetails/${bookingId}`,
      }),

      getManageInfo: builder.query({
        query: (manageBookingToken) =>
          `getMybookings?manageBookingToken=${manageBookingToken}`,
      }),

      // ── Ticket booking flow mutations ──────────────────────────────

      sendOtpToAddParticipant: builder.mutation({
        query: (data) => ({
          url: "/sendOtpToAddParticipant",
          method: "POST",
          body: data,
        }),
      }),

      verifyOtpToAddParticipant: builder.mutation({
        query: ({ code, token }) => ({
          url: "/verifyOtpToAddParticipant",
          method: "POST",
          body: { code, token },
        }),
      }),

      verifyPrivateBookingCode: builder.mutation({
        query: ({ inviteCode, classId }) => ({
          url: "/schools/verifyInviteCode",
          method: "PUT",
          body: { inviteCode, classId },
        }),
      }),

      createBookingReservation: builder.mutation({
        query: ({ eventScheduleId, selectedEventPricing }) => ({
          url: "/saveEventReservations",
          method: "POST",
          body: { eventScheduleId, selectedEventPricing },
        }),
      }),

      updateBookingReservation: builder.mutation({
        query: ({ bookingsReservationId, selectedEventPricing }) => ({
          url: "/updateEventReservations",
          method: "POST",
          body: { bookingsReservationId, selectedEventPricing },
        }),
      }),

      checkTikkiePayStatus: builder.mutation({
        query: ({ paymentRequestToken }) => ({
          url: "/payments/checkLinkPaymentStatus",
          method: "POST",
          body: { paymentRequestToken },
        }),
      }),

      verifyOtp: builder.mutation({
        query: ({ code, phoneVerificationToken }) => ({
          url: "/verifyOtpToAddParticipant",
          method: "POST",
          body: { code, token: phoneVerificationToken },
        }),
      }),
    };
  },
});

export const {
  useGetTicketsInfoQuery,
  useGetPaymentProfileQuery,
  useGetBookingConfirmationQuery,
  useGetValidateWebLinkQuery,
  useGetEventDetailsQuery,
  useResolveTrackTokenQuery,
  useGetBookingInfoQuery,
  useGetManageInfoQuery,
  useCheckStripePaymentStatusMutation,
  useCreateTikkiePaymentLinkMutation,
  useSendEmailMutation,
  useSendOtpToAddParticipantMutation,
  useVerifyOtpToAddParticipantMutation,
  useVerifyPrivateBookingCodeMutation,
  useCreateBookingReservationMutation,
  useUpdateBookingReservationMutation,
  useCheckTikkiePayStatusMutation,
  useVerifyOtpMutation,
} = ticketServices;

export default ticketServices;
